import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import JsonLd from "@/components/base/JsonLd";
import MangaCard from "@/components/base/MangaCard";
import { mangaList } from "@/mocks/manga";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
            <h1 className="text-2xl font-bold text-foreground-950 mt-4 font-heading">漫画未找到</h1>
            <p className="text-foreground-500 mt-2">抱歉，该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = mangaList
    .filter((m) => m.category === manga.category && m.id !== manga.id)
    .slice(0, 4);

  const categoryLabels: Record<string, string> = {
    hanman: "韩漫",
    riman: "日漫",
    guoman: "国漫",
  };

  return (
    <>
      <ScrollToTop />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": manga.title,
          "author": { "@type": "Person", "name": manga.author },
          "datePublished": manga.updateDate,
          "description": manga.description,
          "image": manga.cover,
          "publisher": {
            "@type": "Organization",
            "name": "皮皮漫画网",
            "url": "https://pipimanhua.com/",
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://pipimanhua.com/article/${manga.id}`,
          },
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://pipimanhua.com/",
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": categoryLabels[manga.category],
              "item": `https://pipimanhua.com/category/${manga.category}`,
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": manga.title,
            },
          ],
        }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            {
              label: categoryLabels[manga.category] || manga.categoryLabel,
              to: `/category/${manga.category}`,
            },
            { label: manga.title },
          ]}
        />

        <article className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="flex-shrink-0 mx-auto lg:mx-0">
              <div className="w-56 md:w-64 aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={manga.cover}
                  alt={manga.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {manga.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i> {manga.rating} 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {(manga.views / 10000).toFixed(0)}万 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-book-open-line"></i> {manga.chapters} 话
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    manga.status === "连载中"
                      ? "bg-accent-100 text-accent-800"
                      : "bg-foreground-100 text-foreground-600"
                  }`}
                >
                  {manga.status}
                </span>
              </div>

              <div className="mt-6 p-5 bg-background-100 rounded-xl">
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line"></i> 开始阅读
                </button>
                <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i> 加入收藏
                </button>
                <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-line"></i> 分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-bold text-foreground-950 mb-6 font-heading">章节列表</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {Array.from({ length: Math.min(manga.chapters, 50) }, (_, i) => (
                <button
                  key={i}
                  className="flex items-center justify-between bg-background-100 hover:bg-background-200 rounded-lg px-4 py-3 text-sm transition-colors cursor-pointer"
                >
                  <span className="text-foreground-700 font-medium">
                    第 {manga.chapters - i} 话
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </button>
              ))}
            </div>
            {manga.chapters > 50 && (
              <p className="text-center text-sm text-foreground-500 mt-4">
                仅展示最近 50 话，共 {manga.chapters} 话
              </p>
            )}
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-bold text-foreground-950 mb-6 font-heading">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {related.map((m) => (
                  <MangaCard key={m.id} {...m} />
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}